import { Trans, useLingui } from '@lingui/react';
import * as React from 'react';
import { GenericError } from '@startuptools/ui/Error';
import { DateFormat } from '@startuptools/ui/DateFormat';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import { DialogTitle, DialogActions, DialogContent } from '@startuptools/ui/Dialog';
import { NumberFormat } from '@startuptools/ui/NumberFormat';
import { TransactionsViewDocument, useTransactionsViewSuspenseQuery } from '../transactions-view.graphql';
import { Button } from '@startuptools/ui/Button';
import { FormControl } from '@startuptools/ui/FormControl';
import { FormLabel } from '@startuptools/ui/FormLabel';
import { Typography } from '@startuptools/ui/Typography';
import { Table } from '@startuptools/ui/Table';
import { IconButton } from '@startuptools/ui/IconButton';
import { ButtonGroup } from '@startuptools/ui/ButtonGroup';
import { Box } from '@startuptools/ui/Box';
import { DateTime } from 'luxon';
import { type GqlCompany, type GqlShareholder } from '../../../../graphql/base-types.graphql';
import { Controller, useForm } from 'react-hook-form';
import { DateInput } from '@startuptools/ui/DateInput';
import {
  useCreateShareholderMutation,
  useSnrCasesSuspenseQuery,
  useStgSetupMutation,
} from '../../../../graphql/react-operations';
import { JuridicalKind } from '../../../../models/identity.model';
import { InputState } from '@startuptools/business-ui/identity/types';
import { identityToInput } from '@startuptools/business-ui/identity/identity-to-input';
import { type DisplayIdentity, Identity } from '@startuptools/business-ui/identity/Identity';
import { useCompanyId } from '@startuptools/common/react-wrapper';
import { cloneDeep, orderBy, toNumber } from 'lodash-es';
import { EditQuantityDialog } from './EditQuantityDialog';
import { AddIdentity } from '@startuptools/business-ui/identity/add/AddIdentity';

type FormData = {
  date: DateTime;
  shareholders: (Pick<GqlShareholder, 'id'> & { identity?: DisplayIdentity | undefined | null; shares: number })[];
};

export const SuggestShelfCompanySetup = ({
  transferDate,
  close,
  startShelfCompanySetup,
  startRegularSetup,
}: {
  transferDate: DateTime;
  close: () => void;
  startShelfCompanySetup: () => void;
  startRegularSetup: () => void;
}) => {
  const { i18n } = useLingui();
  const companyId = useCompanyId();
  const { data } = useTransactionsViewSuspenseQuery({ variables: { companyId }, fetchPolicy: 'cache-and-network' });

  return (
    <>
      <DialogTitle>
        <Trans id="Setup automatically?" />
      </DialogTitle>
      <DialogContent>
        <Typography>
          <Trans
            id="It seems that {company} was founded as a shelf company {foundationDate} and sold to those who started the business on {transferDate} (or just before). Is that correct?"
            values={{
              foundationDate: <DateFormat locale={i18n.locale}>{data.company.foundationDate}</DateFormat>,
              transferDate: <DateFormat locale={i18n.locale}>{transferDate}</DateFormat>,
              company: data.company.name,
            }}
          />
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" color="neutral" onClick={close}>
          <Trans id="Cancel" />
        </Button>
        <Button variant="outlined" color="neutral" onClick={startRegularSetup}>
          <Trans id="Add share register manually" />
        </Button>
        <Button onClick={startShelfCompanySetup}>
          <Trans id="Continue" />
        </Button>
      </DialogActions>
    </>
  );
};

export const CreateShelfCompanyForm = ({
  company,
  close,
}: {
  company: Pick<GqlCompany, 'foundationDate' | 'id' | 'name'>;
  close: () => void;
}) => {
  const [setup, { loading, error }] = useStgSetupMutation({
    refetchQueries: [TransactionsViewDocument],
  });

  // eslint-disable-next-line sut/loading-mutation
  const [createShareholder] = useCreateShareholderMutation();
  const { data: snrData } = useSnrCasesSuspenseQuery({ variables: { companyId: company.id } });
  const { data } = useTransactionsViewSuspenseQuery({
    variables: { companyId: company.id },
    fetchPolicy: 'cache-and-network',
  });

  const snrCases = orderBy(snrData.snrCases.models, c => c.arrivalDate, 'asc');

  // @ts-expect-error TS2532
  const minDate = DateTime.fromISO(snrCases[0].arrivalDate!);
  // @ts-expect-error TS2532
  const maxDate = DateTime.fromISO(snrCases[1].arrivalDate!);

  const diff = maxDate.diff(minDate, 'days');
  const defaultDate = maxDate.minus({ days: Math.min(7, diff.days) });

  const { register, handleSubmit, control, watch, setValue, formState } = useForm<FormData>({
    defaultValues: {
      date: defaultDate,
      shareholders: [],
    },
  });

  const save = async (data: FormData) => {
    const transactionDate = data.date.toUTC(0, { keepLocalTime: true }).toISO();
    if (!transactionDate) {
      throw new Error('no date found');
    }
    await setup({
      variables: {
        companyId: company.id,
        input: {
          transferDate: transactionDate,
          transfers: data.shareholders.map(sh => ({ buyerId: sh.id, numberOfShares: sh.shares })),
        },
      },
    });

    close();
  };

  const shareholders = watch('shareholders');

  const { i18n } = useLingui();

  const [showIdentityDialog, setShowIdentityDialog] = React.useState(false);
  const [editQuantityForId, setEditQuantityForId] = React.useState<false | string>(false);

  const sharesAtFoundation = toNumber(
    data.snrShareCapitalChanges.find(scc => scc.name === 'NYBILDNING')?.sharesTotal ?? 0,
  );
  const sharesLeftToDistribute = sharesAtFoundation - shareholders.reduce((acc, sh) => acc + sh.shares, 0);

  register('shareholders', {
    validate: shareholders => shareholders.reduce((acc, sh) => acc + sh.shares, 0) === sharesAtFoundation,
  });

  return (
    <>
      <DialogTitle onClose={close}>
        <Trans id="Configure setup" />
      </DialogTitle>
      {editQuantityForId && (
        <EditQuantityDialog
          defaultValue={shareholders.find(sh => sh.id === editQuantityForId)?.shares}
          close={() => {
            setEditQuantityForId(false);
          }}
          sharesLeftToDistribute={sharesLeftToDistribute}
          onSave={({ quantity }) => {
            const sh = cloneDeep(shareholders);
            const i = shareholders.findIndex(sh => sh.id === editQuantityForId);
            if (i >= 0) {
              // @ts-expect-error TS2532
              sh[i].shares = parseInt(quantity);
            }

            setValue('shareholders', sh);
            setEditQuantityForId(false);
          }}
        />
      )}
      <form onSubmit={handleSubmit(save)}>
        <DialogContent>
          <>
            <FormControl>
              <FormLabel>
                <Trans id="Date" />
              </FormLabel>
              <Typography fontSize="sm">
                <Trans id="When was the company purchased from the shelf company supplier?" />
              </Typography>
              <Controller
                control={control}
                name="date"
                render={({ field }) => (
                  <DateInput
                    {...field}
                    minDate={minDate}
                    maxDate={maxDate}
                    value={field.value}
                    onChange={newValue => {
                      field.onChange(newValue);
                    }}
                    disabled={loading}
                  />
                )}
              />
            </FormControl>
            <FormControl>
              <FormLabel>
                <Trans id="Shareholders" />
              </FormLabel>
              <Typography fontSize="sm">
                <Trans id="Here you add the shareholders after the transaction from the shelf company supplier." />
              </Typography>
              {formState.errors.shareholders && (
                <Typography color="danger" fontSize="sm">
                  <Trans id="Make sure to assign exactly {count} shares" values={{ count: sharesAtFoundation }} />
                </Typography>
              )}

              <Box mt={2}>
                <Button
                  onClick={() => {
                    setShowIdentityDialog(true);
                  }}
                >
                  <Trans id="Add shareholder" />
                </Button>
              </Box>

              {showIdentityDialog && (
                <AddIdentity
                  headline={<Trans id="Choose buyer" />}
                  onClose={() => setShowIdentityDialog(false)}
                  handleCreate={async identity => {
                    const { data } = await createShareholder({
                      variables: {
                        companyId: company.id,
                        input: {
                          companyId: company.id,
                          identityInput: identityToInput(identity),
                        },
                      },
                    });
                    if (data?.createShareholder) {
                      if (!shareholders.some(s => s.id === data.createShareholder.id)) {
                        setValue('shareholders', [...shareholders, { ...data.createShareholder, shares: 0 }]);
                      }
                      setEditQuantityForId(data.createShareholder.id);
                    }
                    setShowIdentityDialog(false);
                  }}
                  juridicalKinds={[JuridicalKind.Person, JuridicalKind.Company]}
                  relations={{ companyDirector: true, shareholder: true }}
                  availableInputStates={{
                    [InputState.CompanyBoard]: false,
                    [InputState.System]: true,
                    [InputState.Swedish]: true,
                    [InputState.Foreign]: true,
                  }}
                />
              )}
              {Boolean(shareholders.length) && (
                <Table>
                  <thead>
                    <tr>
                      <th colSpan={3}>
                        <Trans id="Shareholder" />
                      </th>
                      <th align="right">
                        <Trans id="Quantity" />
                      </th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {shareholders.map(sh => (
                      <tr key={sh.id}>
                        <td colSpan={3}>{sh.identity && <Identity identity={sh.identity} />}</td>
                        <td align="right">
                          <NumberFormat maximumFractionDigits={0} locale={i18n.locale}>
                            {sh.shares}
                          </NumberFormat>
                        </td>
                        <th>
                          <ButtonGroup>
                            <IconButton
                              color="danger"
                              onClick={() => {
                                setValue(
                                  'shareholders',
                                  shareholders.filter(s => s.id !== sh.id),
                                );
                              }}
                            >
                              <DeleteIcon />
                            </IconButton>
                            <IconButton
                              color="primary"
                              onClick={() => {
                                setEditQuantityForId(sh.id);
                              }}
                            >
                              <EditIcon />
                            </IconButton>
                          </ButtonGroup>
                        </th>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr>
                      <td colSpan={3}></td>
                      <td align="right">
                        <NumberFormat maximumFractionDigits={0} locale={i18n.locale}>
                          {shareholders.reduce((acc, sh) => acc + sh.shares, 0)}
                        </NumberFormat>
                      </td>
                      <td></td>
                    </tr>
                  </tfoot>
                </Table>
              )}
            </FormControl>
          </>

          {error && <GenericError />}
        </DialogContent>
        <DialogActions>
          <Button variant="plain" color="neutral" disabled={loading} onClick={close}>
            <Trans id="Cancel" />
          </Button>
          <Button type="submit" loading={loading}>
            <Trans id="Confirm" />
          </Button>
        </DialogActions>
      </form>
    </>
  );
};
